import { useEffect, useRef } from 'preact/hooks';
import { activeCard, ui, updateUi } from '../state/store';
import { newCard } from '../state/actions';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { ConversationPanel } from './ConversationPanel';
import { Workspace } from './Workspace';
import { SettingsDialog } from './SettingsDialog';
import { Toast } from './Toast';
import { MobileTabs } from './MobileTabs';
import { PanelBoundary } from './PanelBoundary';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function App() {
  const state = ui.value;
  const card = activeCard.value;
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey;
      if (mod && event.key.toLowerCase() === 'b' && !isEditableTarget(event.target)) {
        event.preventDefault();
        updateUi({ sidebarOpen: !ui.value.sidebarOpen });
      } else if (mod && event.key === ',') {
        event.preventDefault();
        updateUi({ settingsOpen: true });
      } else if (event.altKey && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        void newCard();
      } else if (event.key === 'Escape') {
        if (ui.value.inlineEditOpen) updateUi({ inlineEditOpen: false });
        else if (window.innerWidth < 800 && ui.value.sidebarOpen) updateUi({ sidebarOpen: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const startDrag = (event: PointerEvent) => {
    const main = mainRef.current;
    if (!main) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    const rect = main.getBoundingClientRect();
    const move = (e: PointerEvent) => {
      const ratio = Math.min(0.7, Math.max(0.25, (e.clientX - rect.left) / rect.width));
      main.style.setProperty('--split', String(ratio));
    };
    const up = (e: PointerEvent) => {
      handle.releasePointerCapture(e.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      const ratio = parseFloat(main.style.getPropertyValue('--split')) || ui.value.splitRatio;
      updateUi({ splitRatio: ratio });
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
  };

  return (
    <div class="app" data-mobile-tab={state.mobileTab} data-sidebar={state.sidebarOpen ? 'open' : 'closed'}>
      <h1 class="sr-only">Cue</h1>
      <TopBar />
      <div class="body">
        <Sidebar />
        {state.sidebarOpen && <div class="sidebar-backdrop" onClick={() => updateUi({ sidebarOpen: false })} />}
        <main class="main" ref={mainRef} style={{ '--split': String(state.splitRatio) }}>
          {card ? (
            <>
              <PanelBoundary name="conversation">
                <ConversationPanel />
              </PanelBoundary>
              <div
                class="divider"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize the conversation and the workspace"
                onPointerDown={startDrag}
              />
              <PanelBoundary name="editor">
                <Workspace />
              </PanelBoundary>
            </>
          ) : (
            <div class="loading-screen">Loading…</div>
          )}
        </main>
      </div>
      <MobileTabs />
      <SettingsDialog />
      <Toast />
    </div>
  );
}
